import { useEffect, useState } from "react";
import { g, eur } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { PremiumLock } from "@/components/PremiumLock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function Statistiche() {
  const { has } = usePlan();
  const { t } = useI18n();
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);

  useEffect(() => {
    if (has("reports")) g("/stats", { period }).then(setD).catch(() => {});
  }, [period, has]);

  if (!has("reports"))
    return (
      <PremiumLock
        title={t("st.lockTitle")}
        description={t("st.lockDesc")}
        needed="pro"
        message={t("st.lockMsg")}
        preview={
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[[t("st.revenue"), eur(18420)], [t("st.covers"), "412"], [t("st.avgTicket"), eur(44.7)]].map(([l, v]) => (
                <Card key={l} className="p-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
                  <p className="font-num mt-3 text-2xl font-bold">{v}</p>
                </Card>
              ))}
            </div>
            <Card className="p-6">
              <div className="flex h-40 items-end gap-2">
                {[40, 62, 55, 78, 91, 70, 84].map((h, i) => (
                  <div key={`bar-${i}-${h}`} className="flex-1 rounded-t bg-slate-900" style={{ height: `${h}%` }} />
                ))}
              </div>
            </Card>
          </div>
        }
      />
    );

  if (!d) return <p className="text-sm text-slate-500">{t("g.loading")}</p>;
  const max = Math.max(1, ...d.series.map((s) => s.total));
  const maxD = Math.max(1, ...d.top_dishes.map((x) => x.qty));

  return (
    <div className="fade-up space-y-6" data-testid="statistiche-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("st.plan")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("st.title")}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={period === "week" ? "default" : "outline"} onClick={() => setPeriod("week")} data-testid="period-week-btn">
            {t("st.week")}
          </Button>
          <Button variant={period === "month" ? "default" : "outline"} onClick={() => setPeriod("month")} data-testid="period-month-btn">
            {t("st.month")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["revenue", t("st.revenue"), eur(d.total)], ["covers", t("st.covers"), d.covers], ["avg", t("st.avgTicket"), eur(d.avg_ticket)]].map(([id, l, v]) => (
          <Card key={id} className="p-6" data-testid={`stat-${id}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
            <p className="font-num mt-3 text-3xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      <Card className="p-6" data-testid="stat-series">
        <h3 className="text-lg font-semibold">{t("st.series")}</h3>
        <div className="mt-6 flex h-52 items-end gap-2">
          {d.series.map((s) => (
            <div key={s.day} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
              <span className="font-num text-[10px] text-slate-500">{Math.round(s.total)}</span>
              <div className="w-full rounded-t bg-slate-900" style={{ height: `${(s.total / max) * 100}%`, minHeight: 4 }} />
              <span className="font-num text-[10px] text-slate-500">{s.day.slice(5)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6" data-testid="stat-top-dishes">
        <h3 className="text-lg font-semibold">{t("st.best")}</h3>
        <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {d.top_dishes.map((x) => (
            <li key={x.name}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm font-medium">{x.name}</span>
                <span className="font-num text-sm text-slate-500">{x.qty}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(x.qty / maxD) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
